import React, { useEffect, useMemo, useState } from 'react'
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Button } from '@mui/material'
import { useAuth } from '../contexts/AuthContext';
import { useIngredient } from '../contexts/IngredientContext';
import { productNav } from '../components/NavigationPath';
import AddIngredient from '../components/AddIngredient';
import AddIngredientStock from '../components/AddIngredientStock';
import Layout2 from '../components/Layout2';
import Loader from '../components/Loader';
import moment from 'moment';
import PageHeader from '../components/PageHeader';

function IngredientsList() {
    const { getAllIngredients, deleteIngredient } = useIngredient()
    const { restaurantId, auth, hasPermission } = useAuth();

    const canCreate = useMemo(() => hasPermission("products", "create"),  [hasPermission]);
    const canUpdate = useMemo(() => hasPermission("products", "update"), [hasPermission]);
    const canDelete = useMemo(() => hasPermission("products", "delete"), [hasPermission]);

    const [ingredientsList, setIngredientList] = useState()
    const [sortField, setSortField] = useState('createdAt');
    const [sortOrder, setSortOrder] = useState(-1);
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [ingredientVisible, setIngredientVisible] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [editIngredientId, setEditIngredientId] = useState(null);
    const [addStockVisible, setAddStockVisible] = useState(false);

    const fetchIngredients = async () => {
        try {
            setIsLoading(true);
            const devices = await getAllIngredients(currentPage, rowsPerPage, sortField, sortOrder, null);
            if (devices.length !== 0) {
                setIngredientList(devices.data)
                setTotalRecords(devices.totalCount)
            }
            setIsLoading(false);
        } catch (error) {
            console.error('Error fetching ingredients', error);
        }
    };

    useEffect(() => {
        if (restaurantId) {
            fetchIngredients();
        }
    }, [currentPage, rowsPerPage, sortField, sortOrder, restaurantId]);

    const handleSorting = async (e) => {
        const field = e.sortField;
        const order = e.sortOrder;

        setSortField(field);
        setSortOrder(order);
        fetchIngredients()
    };

    const onPageChange = (event) => {
        const newCurrentPage = Math.floor(event.first / event.rows) + 1;
        setCurrentPage(newCurrentPage);
        const newRowsPerPage = event.rows;
        setRowsPerPage(newRowsPerPage);
    };

    const handleDelete = async (ingredientId) => {
        try {
            const confirmed = await confirmDelete();

            if (confirmed) {
                await deleteIngredient(ingredientId);
                fetchIngredients();
            }
        } catch (error) {
            console.error('Error during delete operation', error);
        }
    }

    const confirmDelete = async () => {
        return new Promise((resolve) => {
            confirmDialog({
                message: 'Are you sure you want to delete this ingredient?',
                header: 'Delete Confirmation',
                icon: 'pi pi-info-circle',
                position: 'top',
                accept: () => resolve(true),
                reject: () => resolve(false),
            });
        });
    };

    const handleUpdate = async (ingredientId) => {
        setEditIngredientId(ingredientId);
        setEditMode(true);
        setIngredientVisible(false);
    }

    const handleStock = async (ingredientId) => {
        setEditIngredientId(ingredientId);
        setAddStockVisible(true)
    }

    return (
        <Layout2 items={productNav.items} navTitle={productNav.header}>
            <div className="d-flex flex-column w-100">
                {/* <div className='d-flex flex-row px-4 py-2 w-100 border-bottom'>
                    <h4>Ingredients</h4>
                    <div className='d-flex justify-content-end w-100'>
                        <div className='align-self-end header_btn_container'>
                            <Button
                                variant="outlined"
                                color='success'
                                type="submit"
                                onClick={() => setIngredientVisible(true)}
                            >
                                Add New Ingredient
                            </Button>
                        </div>
                    </div>
                </div> */}
                <PageHeader
                    title="Ingredients"
                    buttonLable={canCreate ? "Add New Ingredient" : undefined}
                    buttonOnClick={canCreate ? () => setIngredientVisible(true) : undefined}
                />
                <div className='px-4 py-2'>
                    <ConfirmDialog />
                    {(ingredientVisible || (editMode && editIngredientId)) && (
                        <AddIngredient
                            editMode={editMode}
                            editIngredientId={editIngredientId}
                            setEditIngredientId={setEditIngredientId}
                            setEditMode={setEditMode}
                            ingredientVisible={ingredientVisible}
                            setIngredientVisible={setIngredientVisible}
                            fetchIngredients={fetchIngredients}
                        />
                    )}
                    {(addStockVisible && editIngredientId) && (
                        <AddIngredientStock
                            editIngredientId={editIngredientId}
                            setEditIngredientId={setEditIngredientId}
                            addStockVisible={addStockVisible}
                            setAddStockVisible={setAddStockVisible}
                            fetchIngredients={fetchIngredients}
                        />
                    )}
                    {isLoading ? (
                        <Loader />
                    ) : (
                        <DataTable
                            className="customerTable w-100 mt-4"
                            totalRecords={totalRecords}
                            lazy
                            paginator
                            sortField={sortField}
                            sortOrder={sortOrder}
                            onSort={(e) => {
                                if (!e.sortField && e.sortOrder === 0) {
                                    handleSorting({ sortField: 'createdAt', sortOrder: -1 });
                                } else {
                                    handleSorting(e);
                                }
                            }}
                            removableSort
                            rows={rowsPerPage}
                            value={ingredientsList}
                            first={(currentPage - 1) * rowsPerPage}
                            onPage={onPageChange}
                            dataKey="id"
                            emptyMessage="No Ingredients found."
                        >
                            <Column
                                field="name"
                                header="Name"
                                sortable
                                filterField="name"
                                body={(rowData) => (
                                    <div className='name' title={rowData.name}>
                                        {rowData.name}
                                    </div>
                                )}
                            />
                            <Column
                                field="available_stock"
                                header="Available Stock"
                                sortable
                                filterField="available_stock"
                                body={(rowData) => (
                                    <div>
                                        {`${rowData.available_stock} ${rowData.unit}`}
                                    </div>
                                )}
                            />
                            <Column
                                field="description"
                                header="Description"
                                filterField="description"
                                body={(rowData) => (
                                    <div className='description' title={rowData.description}>
                                        {rowData.description}
                                    </div>
                                )}
                            />
                            <Column
                                field="expiry_date"
                                header="Expiry Date"
                                filterField="expiry_date"
                                body={(rowData) => (
                                    <>
                                        {rowData.expiry_date ?
                                            <div className={moment(rowData.expiry_date).diff(moment(), 'days') <= 2 ? 'expire-soon expiry_date' : 'expiry_date'}>
                                                {`${moment(rowData.expiry_date).format('Y-MM-DD')}`}
                                            </div>
                                            : ""
                                        }
                                    </>
                                )}
                            />
                            {(canUpdate || canDelete) && <Column
                                header="Action"
                                className='action_td'
                                body={(rowData) => (
                                    <div className='action'>
                                        {
                                            canUpdate &&
                                            <>
                                                <i className="pi pi-plus pointerCursor" title='Add New Stock' onClick={() => handleStock(rowData.id)} />
                                                <i className="pi pi-pencil ms-2 pointerCursor" title='Edit' onClick={() => handleUpdate(rowData.id)} />
                                            </>
                                        }
                                        {canDelete && <i className="pi pi-trash ms-2 pointerCursor" title='Delete' aria-label="Cancel" onClick={() => handleDelete(rowData.id)} />}
                                    </div>
                                )}
                            />}
                        </DataTable>
                    )}
                </div>
            </div>
        </Layout2>
    )
}

export default IngredientsList